import { useState } from "react";
import { Gift, Copy, Check, Share2, Mail, MessageCircle, ArrowRight, ShieldOff } from "lucide-react";
import { toast } from "sonner";

/**
 * Refer-a-friend panel for the customer Account page.
 *
 * Single-use referral model:
 *   - Each customer gets ONE unique referral code (stable across sessions).
 *   - When a friend uses it at monthly-plan checkout, their first month is free.
 *   - Once redeemed, the code is voided and cannot be reused.
 *
 * Hydrates from `/api/billing/referral-info` which returns:
 *   { referral_code, status, is_active, redeemed_by_email_masked,
 *     redeemed_at, share_url, share_messages }
 */
export default function ReferAFriend({ referral }) {
  const [copied, setCopied] = useState({ code: false, link: false });

  if (!referral?.referral_code) return null;

  const isActive = !!referral.is_active;
  const status = (referral.status || "active").toLowerCase();
  const redeemedBy = referral.redeemed_by_email_masked || "";
  const redeemedAt = referral.redeemed_at || "";
  const share = referral.share_messages || {};
  const url = referral.share_url || "";

  const copy = async (text, kind) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied((p) => ({ ...p, [kind]: true }));
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied((p) => ({ ...p, [kind]: false })), 1800);
    } catch {
      toast.error("Couldn't copy - long-press to copy manually");
    }
  };

  const shareNative = async () => {
    if (!isActive) return;
    const text = share.whatsapp || "";
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Protect your UK business name",
          text,
          url,
        });
      } catch {
        /* user cancelled */
      }
    } else {
      copy(`${text}`, "link");
    }
  };

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(share.whatsapp || "")}`;
  const emailHref = `mailto:?subject=${encodeURIComponent(share.email_subject || "")}&body=${encodeURIComponent(share.email_body || "")}`;

  return (
    <div className="mt-6 rounded-3xl overflow-hidden border-2 border-[#04273d]/10 bg-white" data-testid="refer-a-friend-panel">
      {/* Header */}
      <div className={`text-white p-6 md:p-8 ${isActive ? "bg-gradient-to-br from-[#04273d] via-[#0d4d6e] to-[#0d9b80]" : "bg-gradient-to-br from-[#04273d] via-[#26384a] to-[#3d4d5d]"}`}>
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center shrink-0 border border-white/20">
            {isActive ? <Gift size={22} className="text-[#71edc9]" /> : <ShieldOff size={22} className="text-white/60" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-mono-brand text-[10.5px] uppercase tracking-widest text-[#71edc9]">Refer a friend</p>
            <h2 className="font-display text-2xl md:text-3xl font-bold mt-1 leading-tight">
              {isActive ? "Gift a friend their first month free" : "Your referral code has been used"}
            </h2>
            <p className="text-sm text-white/80 mt-2 leading-relaxed">
              {isActive
                ? "Your one-time referral code gives a friend their first month free on the monthly plan. Once redeemed it&rsquo;s voided permanently &mdash; so make it count."
                : `Code redeemed${redeemedBy ? ` by ${redeemedBy}` : ""}${redeemedAt ? ` on ${new Date(redeemedAt).toLocaleDateString("en-GB")}` : ""}. Single-use codes can${"\u2019"}t be reused - thank you for the introduction.`}
            </p>
          </div>
        </div>

        {/* Code + status */}
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <div className={`flex-1 backdrop-blur-sm border rounded-2xl px-4 py-3.5 flex items-center gap-3 min-w-0 ${isActive ? "bg-white/10 border-white/20" : "bg-white/5 border-white/15"}`}>
            <p className="font-mono-brand text-[10px] uppercase tracking-widest text-[#71edc9] shrink-0">Your code</p>
            <span className={`font-mono-brand text-lg md:text-xl font-bold tracking-wider flex-1 truncate ${isActive ? "text-white" : "text-white/50 line-through"}`} data-testid="refer-friend-code">
              {referral.referral_code}
            </span>
            {isActive ? (
              <button
                onClick={() => copy(referral.referral_code, "code")}
                className="text-white/80 hover:text-[#71edc9] transition-colors shrink-0"
                aria-label="Copy code"
                data-testid="refer-friend-copy-code"
              >
                {copied.code ? <Check size={18} className="text-[#71edc9]" /> : <Copy size={18} />}
              </button>
            ) : (
              <span className="font-mono-brand text-[10px] uppercase tracking-widest text-amber-300 shrink-0" data-testid="refer-friend-status-badge">
                {status === "pending_redemption" ? "Pending" : "Voided"}
              </span>
            )}
          </div>
          {isActive && (
            <button
              onClick={shareNative}
              className="inline-flex items-center justify-center gap-2 bg-[#71edc9] hover:bg-[#71edc9]/85 text-[#04273d] font-display font-bold px-5 py-3.5 rounded-2xl transition-colors shrink-0"
              data-testid="refer-friend-share-button"
            >
              <Share2 size={16} /> Share
            </button>
          )}
        </div>

        {/* Rules strip */}
        <div className="mt-5 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-[12px] text-white/85 leading-relaxed">
          <p className="font-display font-semibold text-white">How it works</p>
          <ul className="mt-1.5 space-y-1 list-disc list-inside marker:text-[#71edc9]">
            <li>Single-use - once redeemed, your code is voided permanently</li>
            <li>Monthly plan only &mdash; annual subscriptions aren&rsquo;t eligible for the referral discount</li>
            <li>Your friend gets their first month free; nothing else changes for you</li>
          </ul>
        </div>
      </div>

      {/* Body - share channels, only when code is active */}
      {isActive && (
        <div className="p-6 md:p-8" data-testid="refer-friend-share-section">
          <p className="font-display font-semibold text-[#04273d] mb-3">Share in one tap</p>
          <div className="grid sm:grid-cols-3 gap-2.5">
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-2xl border-2 border-[#25D366]/30 bg-[#25D366]/5 hover:bg-[#25D366]/12 transition-colors px-4 py-3"
              data-testid="refer-friend-whatsapp"
            >
              <span className="h-9 w-9 rounded-xl bg-[#25D366] text-white flex items-center justify-center shrink-0">
                <MessageCircle size={16} />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block font-display font-semibold text-[#04273d] text-sm">WhatsApp</span>
                <span className="block text-[11px] text-[#447694] truncate">Pre-written message</span>
              </span>
              <ArrowRight size={14} className="text-[#447694] shrink-0" />
            </a>
            <a
              href={emailHref}
              className="flex items-center gap-3 rounded-2xl border-2 border-[#9ac7f7]/40 bg-white hover:border-[#04273d] transition-colors px-4 py-3"
              data-testid="refer-friend-email"
            >
              <span className="h-9 w-9 rounded-xl bg-[#04273d] text-white flex items-center justify-center shrink-0">
                <Mail size={16} />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block font-display font-semibold text-[#04273d] text-sm">Email</span>
                <span className="block text-[11px] text-[#447694] truncate">Opens your mail app</span>
              </span>
              <ArrowRight size={14} className="text-[#447694] shrink-0" />
            </a>
            <button
              onClick={() => copy(url, "link")}
              className="flex items-center gap-3 rounded-2xl border-2 border-[#9ac7f7]/40 bg-white hover:border-[#04273d] transition-colors px-4 py-3 w-full text-left"
              data-testid="refer-friend-copy-link"
            >
              <span className="h-9 w-9 rounded-xl bg-[#447694] text-white flex items-center justify-center shrink-0">
                {copied.link ? <Check size={16} /> : <Copy size={16} />}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block font-display font-semibold text-[#04273d] text-sm">Copy link</span>
                <span className="block text-[11px] text-[#447694] truncate">{url || "protectmybusinessname.co.uk"}</span>
              </span>
            </button>
          </div>

          {/* Preview the message */}
          <details className="mt-4 group">
            <summary className="text-[11.5px] font-display font-semibold text-[#447694] cursor-pointer hover:text-[#04273d] list-none flex items-center gap-1.5">
              <span>Preview the message</span>
              <ArrowRight size={11} className="transition-transform group-open:rotate-90" />
            </summary>
            <p className="mt-2.5 text-[11.5px] text-[#447694] leading-relaxed italic bg-[#f7f7f7] border border-[#9ac7f7]/25 rounded-xl p-3" data-testid="refer-friend-message-preview">
              {share.whatsapp || ""}
            </p>
          </details>
        </div>
      )}
    </div>
  );
}
